import { timezonePlace } from "../events/timezones";
import { dayKeyOf, formatClock, formatDayHeading } from "./agenda-schedule";

/**
 * The Dusk head of the "Now" tab: a "Live" kicker with the date, the time as
 * a very large clock, and the place whose time it is.
 *
 * `at` is the current time, re-read by the tab every 30 seconds and read on
 * the event's clock, so a phone in another zone shows the venue's time.
 *
 * The digits are tabular and the clock sits in a box the width of "88:88", so
 * a minute ticking over never moves what is below. It is not a live region: a
 * screen reader would otherwise announce the time twice a minute.
 */
export function NowHeader({ at, timeZone }: { at: Date; timeZone: string }) {
  const clock = formatClock(at, timeZone);
  const place = `${timezonePlace(timeZone)} time`;
  const date = formatDayHeading(dayKeyOf(at, timeZone));

  return (
    <div
      className="flex min-w-0 flex-col gap-1.5 rounded-2xl border border-border bg-dusk-hero px-5 pb-6 pt-7 text-white dark:border-edge dark:border-lit-edge dark:shadow-lit"
      data-testid="now-header"
    >
      <p
        className="flex items-center gap-2 text-[0.8125rem] uppercase tracking-[0.16em] wrap-anywhere"
        data-testid="now-header-kicker"
      >
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-full bg-hb-yellow shadow-lit-dot motion-safe:animate-pulse"
        />
        Live · {date}
      </p>
      <time
        dateTime={at.toISOString()}
        className="block font-display font-normal leading-[0.95] tracking-tight tabular-nums lining-nums"
        // Scales with the phone, from 320 to the desktop container's cap.
        style={{ fontSize: "clamp(4.5rem, 26vw, 7rem)" }}
        data-testid="now-clock"
      >
        {clock}
      </time>
      <p className="text-base text-white/80" data-testid="now-clock-label">
        {place}
      </p>
    </div>
  );
}
